import {
  Building,
  CalendarClock,
  Crosshair,
  Tag,
  User,
  Users,
} from "lucide-react";
import { FilterLiveForm, useGetList, useTranslate } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";

import { FilterCategory } from "../filters/FilterCategory";
import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sale } from "../types";
import {
  FILTERABLE_TASK_DUE_BUCKETS,
  getTaskDueBucketRailFilter,
  NULL_FILTER_VALUE,
  type TaskDueBucket,
  type TaskFilter,
} from "./taskDueBuckets";

/**
 * Full-text search runs through the `@or` filter both data providers agree on:
 * Supabase turns it into a PostgREST `or=(text.ilike.…)`, FakeRest into its own
 * `q`. A bare `text@ilike` would only work against Supabase.
 */
const SEARCH_SOURCE = "@or.text@ilike";

/**
 * What a task is about, in the order the list is read: the account, the person,
 * then the deal. A task carries at least one of the three (`tasks_has_target`),
 * and a deal or account task does not have to name anybody.
 */
const TASK_TARGETS = ["company", "contact", "deal"] as const;

type TaskTarget = (typeof TASK_TARGETS)[number];

/**
 * The filter a target toggle writes. Every key the group uses is listed,
 * `undefined` on the options that do not need it, so picking a target replaces
 * the whole group instead of intersecting it with the previous choice.
 *
 * "An account" is the task nobody and no deal is named on — the rest of the
 * account's tasks reach it through the person or the deal they are about.
 */
const getTaskTargetRailFilter = (target: TaskTarget): TaskFilter => ({
  "contact_id@is": target === "company" ? NULL_FILTER_VALUE : undefined,
  "deal_id@is": target === "company" ? NULL_FILTER_VALUE : undefined,
  "contact_id@not.is": target === "contact" ? NULL_FILTER_VALUE : undefined,
  "deal_id@not.is": target === "deal" ? NULL_FILTER_VALUE : undefined,
});

const DUE_BUCKET_LABELS: Record<TaskDueBucket, string> = {
  overdue: "resources.tasks.filters.overdue",
  today: "resources.tasks.filters.today",
  tomorrow: "resources.tasks.filters.tomorrow",
  this_week: "resources.tasks.filters.this_week",
  no_due_date: "resources.tasks.filters.no_due_date",
  later: "resources.tasks.filters.later",
  completed: "resources.tasks.filter.done",
};

/**
 * The task list's filter rail, mirroring the one Companies and Contacts have.
 *
 * The owner filter is only worth showing to an administrator: everyone else is
 * already pinned to their own tasks by the list's permanent filter.
 */
export const TasksListFilters = ({ isAdmin }: { isAdmin: boolean }) => {
  const { taskTypes } = useConfigurationContext();
  const translate = useTranslate();

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: isAdmin },
  );

  return (
    <div className="w-52 min-w-52 flex flex-col gap-8">
      <FilterLiveForm>
        <SearchInput
          source={SEARCH_SOURCE}
          placeholder={translate("resources.tasks.filter.search")}
        />
      </FilterLiveForm>

      <FilterCategory
        icon={<CalendarClock className="h-4 w-4" />}
        label="resources.tasks.filter.due"
      >
        {FILTERABLE_TASK_DUE_BUCKETS.map((bucket) => (
          <ToggleFilterButton
            className="w-full justify-between"
            key={bucket}
            label={DUE_BUCKET_LABELS[bucket]}
            value={getTaskDueBucketRailFilter(bucket)}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Crosshair className="h-4 w-4" />}
        label="resources.tasks.filter.target"
      >
        {TASK_TARGETS.map((target) => (
          <ToggleFilterButton
            className="w-full justify-between"
            key={target}
            label={`resources.tasks.target.${target}`}
            value={getTaskTargetRailFilter(target)}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Tag className="h-4 w-4" />}
        label="resources.tasks.filter.type"
      >
        {taskTypes.map((taskType) => (
          <ToggleFilterButton
            className="w-full justify-between"
            key={taskType.value}
            label={taskType.label}
            value={{ type: taskType.value }}
          />
        ))}
      </FilterCategory>

      {isAdmin && (
        <FilterCategory
          icon={<Users className="h-4 w-4" />}
          label="resources.tasks.filter.owner"
        >
          {sales?.map((sale) => (
            <ToggleFilterButton
              className="w-full justify-between"
              key={sale.id}
              label={`${sale.first_name} ${sale.last_name}`}
              value={{ sales_id: sale.id }}
            />
          ))}
        </FilterCategory>
      )}

      <FilterCategory
        icon={<Building className="h-4 w-4" />}
        label="resources.tasks.fields.company_id"
      >
        <FilterLiveForm>
          <ReferenceInput source="company_id" reference="companies">
            <AutocompleteInput
              label={false}
              placeholder={translate("resources.tasks.fields.company_id")}
              helperText={false}
            />
          </ReferenceInput>
        </FilterLiveForm>
      </FilterCategory>

      <FilterCategory
        icon={<User className="h-4 w-4" />}
        label="resources.tasks.fields.contact_id"
      >
        <FilterLiveForm>
          <ReferenceInput source="contact_id" reference="contacts">
            <AutocompleteInput
              label={false}
              placeholder={translate("resources.tasks.fields.contact_id")}
              optionText={contactOptionText}
              helperText={false}
            />
          </ReferenceInput>
        </FilterLiveForm>
      </FilterCategory>
    </div>
  );
};
